import React, { useState } from "react";
import { CheckIcon } from "lucide-react";
import { Dieline } from "../components/Dieline";
import { FactoryQuote } from "../components/FactoryQuote";
import { manufacturingFiles, fileGroups, factoryQuestions, colorwaySpecs } from "../data/manufacturingFiles";
import { renderVsReality } from "../data/factoryQuote";
export function Manufacturing() {
  const [done, setDone] = useState<Record<string, boolean>>({});
  const count = manufacturingFiles.filter(f => done[f.id]).length;
  const pct = Math.round(count / manufacturingFiles.length * 100);
  return <div className="mx-auto max-w-6xl px-5 py-12 md:py-16" data-id="element-229" data-mp="1olue4kxuk3t5">
      <header className="max-w-2xl" data-id="element-230" data-mp="1ok9jywfg2mvt">
        <h1 className="font-display text-5xl font-bold uppercase leading-none tracking-tight text-ink md:text-6xl" data-id="element-231" data-mp="1jn9o1ecb70if">Manufacturing kit</h1>
        <p className="mt-4 text-lg text-muted" data-id="element-232" data-mp="d7qlbi2zwlpo">
          The factory already has the pill crusher mold, so you don't need 3D files. You need print-ready face artwork on their template, a few spec documents, and answers to the questions below.
        </p>
      </header>

      <div className="mt-12" data-id="element-233" data-mp="3pm3jhabyhvbh">
        <FactoryQuote data-id="element-234" data-mp="3pk8xrtsv1z6c" />
      </div>

      <section aria-labelledby="real-h" className="mt-16 rounded-3xl bg-ink p-6 text-paper md:p-10" data-id="element-235" data-mp="3pj7krjsab2lz">
        <h2 id="real-h" className="text-xl font-semibold" data-id="element-236" data-mp="3aqnielcykz5l">Your renders vs. what the factory can print</h2>
        <p className="mt-2 max-w-2xl text-paper/70" data-id="element-237" data-mp="31vzfv2yrpvkh">Your renders are good enough to sell with. Just make sure the finished product matches them, or distributors will notice.</p>
        <dl className="mt-8 grid gap-8 md:grid-cols-3" data-id="element-238" data-mp="36o0qylk79g8x">
          {renderVsReality.map(r => <div key={r.title} data-id="element-239" data-mp="2rzmjtyfr35mk">
              <dt className="font-semibold text-amber" data-id="element-240" data-mp="2wwmtejbt6qif">{r.title}</dt>
              <dd className="mt-2 text-sm text-paper/80" data-id="element-241" data-mp="2wus7p2spquda">{r.text}</dd>
            </div>)}
        </dl>
      </section>

      <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_320px]" data-id="element-242" data-mp="2mygtg79kbug7">
        <section aria-labelledby="files-h" data-id="element-243" data-mp="2n0t8abk0xop1">
          <div className="flex items-end justify-between gap-4" data-id="element-244" data-mp="tghw5yabvyrh">
            <h2 id="files-h" className="text-xl font-semibold text-ink" data-id="element-245" data-mp="thsydto4kygh">Files to send the factory</h2>
            <p className="text-sm text-muted" aria-live="polite" data-id="element-246" data-mp="ongan7fm90yx">{count} of {manufacturingFiles.length} ready</p>
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-mist" data-id="element-247" data-mp="erlnbz0g6hpt">
            <div className="h-full rounded-full bg-ink transition-[width] duration-300 ease-out" style={{
            width: `${pct}%`
          }} data-id="element-248" data-mp="1ceu2zddrowjw" />
          </div>

          {fileGroups.map(g => <div key={g} className="mt-8" data-id="element-249" data-mp="17do8pb4gl189">
              <h3 className="text-sm font-semibold text-muted" data-id="element-250" data-mp="12fwj4nradr78">{g}</h3>
              <ul className="mt-2 divide-y divide-line border-y border-line" data-id="element-251" data-mp="yf2vzwlo4gfe">
                {manufacturingFiles.filter(f => f.group === g).map(f => {
              const checked = !!done[f.id];
              return <li key={f.id} data-id="element-252" data-mp="3j51wxm4app8i">
                      <label className="flex cursor-pointer gap-4 py-4" data-id="element-253" data-mp="3j40cir1e3qe4">
                        <input type="checkbox" className="peer sr-only" checked={checked} onChange={() => setDone(d => ({
                    ...d,
                    [f.id]: !d[f.id]
                  }))} data-id="element-254" data-mp="1yokdeouzxj5b" />
                        <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-ink/30 ${checked ? "border-ink bg-ink text-paper" : "border-line bg-white"}`} data-id="element-255" data-mp="27nxqrsm8qrvb">
                          {checked && <CheckIcon className="h-3.5 w-3.5" data-id="element-256" data-mp="2hfv5vagde3zb" />}
                        </span>
                        <span className="flex flex-col gap-1" data-id="element-257" data-mp="2mey7ekx55s2j">
                          <span className={`font-medium ${checked ? "text-muted line-through" : "text-ink"}`} data-id="element-258" data-mp="2qfi2liy5mlm1">{f.name}</span>
                          <span className="font-mono text-xs text-ink/70" data-id="element-259" data-mp="2vcqo9tiih7s1">{f.format}</span>
                          <span className="text-sm text-muted" data-id="element-260" data-mp="w1z1jz4x0kvz">{f.purpose}</span>
                        </span>
                      </label>
                    </li>;
            })}
              </ul>
            </div>)}
        </section>

        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-261" data-mp="r0tozwa5lb7">
          <div className="rounded-2xl border border-line bg-white p-6" data-id="element-262" data-mp="sltb3xgbufx">
            <h2 className="font-semibold text-ink" data-id="element-263" data-mp="fmqkrcgbooms">Reply to your factory with</h2>
            <ol className="mt-4 flex list-decimal flex-col gap-3 pl-5 text-sm text-muted" data-id="element-264" data-mp="apqd4x3trdtk">
              {factoryQuestions.map(q => <li key={q} data-id="element-265" data-mp="3j4i7abbowgdl">{q}</li>)}
            </ol>
          </div>
        </aside>
      </div>

      <section aria-labelledby="dieline-h" className="mt-16" data-id="element-266" data-mp="3e6yvdi2gsfot">
        <h2 id="dieline-h" className="text-xl font-semibold text-ink" data-id="element-267" data-mp="3sua2n7rm5dj4">How the print template works</h2>
        <div className="mt-6" data-id="element-268" data-mp="3o2aaw2lp5qxm">
          <Dieline accent="#8fb67f" ink="#2d3a2b" data-id="element-269" data-mp="3o20i6799p5o8" />
        </div>
      </section>

      <section aria-labelledby="color-h" className="mt-16" data-id="element-270" data-mp="30cfy1l1do2dd">
        <h2 id="color-h" className="text-xl font-semibold text-ink" data-id="element-271" data-mp="2vew6xkjkzv40">Series 01 color plan</h2>
        <p className="mt-2 text-sm text-muted" data-id="element-272" data-mp="1awvifsu2t0ec">Confirm every code against a physical swatch or factory color chip before approving the sample.</p>
        <div className="mt-6 overflow-x-auto" data-id="element-273" data-mp="1fo3pii7xo9oc">
          <table className="w-full min-w-[560px] text-left text-sm" data-id="element-274" data-mp="10yft9vem017h">
            <thead className="border-b border-ink text-muted" data-id="element-275" data-mp="1108w0zm3q1a7">
              <tr data-id="element-276" data-mp="15yk9dkblutx6">
                <th className="py-3 font-medium" data-id="element-277" data-mp="15y227snemvah">Character</th>
                <th className="py-3 font-medium" data-id="element-278" data-mp="1tml8y3qt300g">Base plastic</th>
                <th className="py-3 font-medium" data-id="element-279" data-mp="1tla9uh8b2gfo">Print</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-280" data-mp="1yjtwykymxy9m">
              {colorwaySpecs.map(c => <tr key={c.id} data-id="element-281" data-mp="1korhb63zkujm">
                  <td className="py-4" data-id="element-282" data-mp="1onh23jrcteda">
                    <span className="flex items-center gap-2 font-semibold text-ink" data-id="element-283" data-mp="1omfg4k8sv7gs">
                      <span className="h-5 w-5 rounded border border-line" style={{
                    backgroundColor: c.swatch
                  }} data-id="element-284" data-mp="96goqknejq38" />
                      {c.name}
                    </span>
                  </td>
                  <td className="py-4 text-ink" data-id="element-285" data-mp="2fu782c7t3lbk">{c.basePlastic}</td>
                  <td className="py-4 text-muted" data-id="element-286" data-mp="2awp5d3kdu2c0">{c.print}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}